import usePrivateAxios from "@/common/api/usePrivateAxios";
import { useMutation } from "@tanstack/react-query";
import { CreateGovernmentAllocationDistributionQuantityValidationSchema, CreateGovernmentAllocationDistributionValidationSchema } from "../schemas/createGovernmentAllocationDistribution.schema";
import { unFormatCurrency, unFormatUnknownUnit } from "@/common/utils/unFormatters";
import { ErrorResponse } from '@/common/types/errorResponse';

type UseCreateGovernmentAllocationDistributionProps = {
  isConsumptionControlledByQuantity?: boolean;
  onSuccess: () => void;
  onError: (error: ErrorResponse) => void;
};

const useCreateGovernmentAllocationDistribution = ({
  onSuccess,
  onError,
}: UseCreateGovernmentAllocationDistributionProps) => {
  const privateAxios = usePrivateAxios();

  return useMutation({
    mutationKey: ['createGovernmentAllocationDistribution'],
    mutationFn: ({
      branchId,
      governmentAllocationId,
      form,
      isConsumptionControlledByQuantity,
    }: {
      branchId?: string;
      governmentAllocationId?: string;
      form: CreateGovernmentAllocationDistributionValidationSchema | CreateGovernmentAllocationDistributionQuantityValidationSchema;
      isConsumptionControlledByQuantity?: boolean;
    }) => {
      if (isConsumptionControlledByQuantity) {
        const quantityForm = form as CreateGovernmentAllocationDistributionQuantityValidationSchema;

        const government_allocation_distribution = {
          total_quantity: quantityForm.total_quantity
            ? unFormatUnknownUnit(quantityForm.total_quantity) >= 0
              ? unFormatUnknownUnit(quantityForm.total_quantity)
              : undefined
            : 0,
          branch_id: branchId,
          government_allocation_id: governmentAllocationId,
          product_categories_distributions_attributes: quantityForm.product_categories_distributions?.map(
            (productCategoryDistribution) => ({
              product_category_id: productCategoryDistribution.product_category.id,
              total_quantity: productCategoryDistribution.total_quantity
                ? unFormatUnknownUnit(productCategoryDistribution.total_quantity) >= 0
                  ? unFormatUnknownUnit(productCategoryDistribution.total_quantity)
                  : undefined
                : 0,
              products_distributions_attributes: productCategoryDistribution.products_distributions?.map(
                (productDistribution) => ({
                  product_id: productDistribution.product.id,
                  total_quantity: productDistribution.total_quantity
                    ? unFormatUnknownUnit(productDistribution.total_quantity) >= 0
                      ? unFormatUnknownUnit(productDistribution.total_quantity)
                      : undefined
                    : 0,
                }),
              ),
            }),
          ),
        };

        return privateAxios.post('/billing/government_allocation_distributions', {
          government_allocation_distribution,
        });
      }

      const currencyForm = form as CreateGovernmentAllocationDistributionValidationSchema;

      const government_allocation_distribution = {
        total_value: currencyForm.total_value ? unFormatCurrency(currencyForm.total_value) : 0,
        branch_id: branchId,
        government_allocation_id: governmentAllocationId,
        product_categories_distributions_attributes: currencyForm.product_categories_distributions?.map((productCategoryDistribution) => ({
          product_category_id: productCategoryDistribution.product_category.id,
          total_value: productCategoryDistribution.total_value ? unFormatCurrency(productCategoryDistribution.total_value) : 0,
          products_distributions_attributes: productCategoryDistribution.products_distributions?.map((productDistribution) => ({
            product_id: productDistribution.product.id,
            total_value: productDistribution.total_value ? unFormatCurrency(productDistribution.total_value) : 0,
          })),
        })),
      };

      return privateAxios.post('/billing/government_allocation_distributions', {
        government_allocation_distribution,
      });
    },
    onSuccess,
    onError,
  });
};

export default useCreateGovernmentAllocationDistribution;
